import "./QuotationRoomBodyItem.scss";
import { useContext, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { Button } from "app/components/ui/Button/Button";
import { FormattedMessage, FormattedPlural } from "react-intl";
import { ImagesCarousel } from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel";
import { htmlSanitize } from "app/utils/sanitize";
import ProgressiveImg from "app/pages/.shared/ProgressiveImg";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import AppGlobalsContext from "app/AppGlobalsContext";
import classNames from "classnames";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ICON_SIZES } from "app/constants";
import PhotoStrokeIcon from "app/icons/navigation/PhotoFilledIcon";

const IMG_SIZES = {
	small: { width: 800, height: 600 },
	medium: { width: 800, height: 600 },
	large: { width: 496, height: 288 },
	xlarge: { width: 496, height: 288 },
};

const MAX_AMENITIES_TO_DISPLAY = 5;

const RoomImage = ({ photos, onRoomImageClick, isMobile }) => {
	if (!photos || photos.length === 0) {
		if (isMobile) {
			return null;
		}

		return (
			<img
				src="/static/icons/no-image.png"
				alt=""
				aria-hidden
				className="quotation-room-body-item__image-unavailable"
				width={248}
				height={144}
			/>
		);
	}

	return isMobile ? (
		<div className="quotation-room-body-item__image-carousel">
			<ImagesCarousel imgSizesMobile={IMG_SIZES} images={photos} showImageIndex />
		</div>
	) : (
		<div className="quotation-room-body-item__image-button" onClick={onRoomImageClick}>
			<ProgressiveImg
				sizes={IMG_SIZES}
				src={photos[0].url}
				thumbnail={photos[0].thumbnail}
				className="quotation-room-body-item__image"
			/>

			<Typography
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				component="span"
				className="quotation-room-body-item__image-count"
			>
				<PhotoStrokeIcon size={ICON_SIZES.MD} />
				<FormattedPlural
					value={photos.length}
					one={
						<FormattedMessage
							id="product.page.photo.button.label.singular"
							values={{
								length: photos.length,
							}}
						/>
					}
					other={
						<FormattedMessage
							id="product.page.photo.button.label.plural"
							values={{
								length: photos.length,
							}}
						/>
					}
				/>
			</Typography>
		</div>
	);
};

RoomImage.propTypes = {
	photos: PropTypes.arrayOf(
		PropTypes.shape({
			url: PropTypes.string,
			thumbnail: PropTypes.string,
		})
	),
	onRoomImageClick: PropTypes.func,
	isMobile: PropTypes.bool,
};

const QuotationRoomBodyItem = ({ room, toggleOpenModal }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const hasDescription = room?.description?.length > 0;
	const hasAmenities = room?.amenities?.length > 0;
	const hasDetails = hasDescription || hasAmenities;

	const detailsRef = useRef();
	const [isDetailsTruncated, setIsDetailsTruncated] = useState(false);

	const { track } = useAnalytics();

	useEffect(() => {
		const checkTruncation = () => {
			if (detailsRef.current) {
				const { clientHeight, scrollHeight } = detailsRef.current;
				setIsDetailsTruncated(scrollHeight > clientHeight);
			}
		};

		checkTruncation();

		window.addEventListener("resize", checkTruncation);

		return () => window.removeEventListener("resize", checkTruncation);
	}, [room.description]);

	const detailsClassName = classNames("quotation-room-body-item__details", {
		"quotation-room-body-item__details--center": !hasDetails,
	});

	const handleOpenModal = () => {
		toggleOpenModal();
		track("room_content_detail_click", {
			roomName: room.title,
		});
	};

	const hasRoomContent = room?.photos?.length > 0 || hasDescription || hasAmenities;

	useEffect(() => {
		if (hasRoomContent) {
			track("room_content_displayed", {
				roomName: room.title,
			});
		}
	}, [hasRoomContent]);

	return (
		(hasDetails || room?.photos?.length > 0) && (
			<article className="quotation-room-body-item" key={room.id}>
				<div className="quotation-room-body-item__room">
					<RoomImage
						photos={room?.photos}
						onRoomImageClick={handleOpenModal}
						isMobile={isMobile}
					/>

					<section className={detailsClassName}>
						{hasAmenities && (
							<ul
								className="quotation-room-body-item__amenities-list"
								ref={detailsRef}
							>
								{room.amenities.slice(0, MAX_AMENITIES_TO_DISPLAY).map(amenity => (
									<li
										key={amenity}
										className="quotation-room-body-item__amenities-item"
									>
										<Typography
											variant={TYPOGRAPHY_VARIANTS.REGULAR}
											component="span"
										>
											{amenity}
										</Typography>
									</li>
								))}
							</ul>
						)}

						{hasDescription && (
							<div
								className="quotation-room-body-item__description"
								ref={detailsRef}
								data-testid="quotation-room-learn-more-button"
								dangerouslySetInnerHTML={{
									__html: htmlSanitize(room.description),
								}}
							/>
						)}

						{!hasDetails && (
							<Typography
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								component="p"
								className="quotation-room-body-item__content-unavailable"
							>
								<FormattedMessage id="room.content.unavailable" />
							</Typography>
						)}

						{(room.amenities.length > MAX_AMENITIES_TO_DISPLAY ||
							(isDetailsTruncated && !isMobile)) && (
							<Button
								variant="borderless"
								onClick={handleOpenModal}
								className="quotation-room-body-item__learn-more-button"
							>
								<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="p">
									<FormattedMessage id="general.learn.more" />
								</Typography>
							</Button>
						)}
					</section>
				</div>
			</article>
		)
	);
};

QuotationRoomBodyItem.propTypes = {
	room: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		photos: PropTypes.arrayOf(
			PropTypes.shape({
				url: PropTypes.string,
				thumbnail: PropTypes.string,
			})
		),
		description: PropTypes.string,
		amenities: PropTypes.arrayOf(PropTypes.string),
		occupancy: PropTypes.shape({
			adults: PropTypes.number,
			children: PropTypes.number,
			babies: PropTypes.number,
		}),
	}),
	toggleOpenModal: PropTypes.func.isRequired,
};

export default QuotationRoomBodyItem;
